import {
  AstridDaoStoreState,
  Decimal18,
  ERC20TokenConversion,
  ERC20TokenConversionChange
} from "@astrid-dao/lib-base";

import { TOKEN_PRECISION } from "../../../constants";
import { BASE_COLLAT } from "../../../strings";
import { ActionDescription, Amount } from "../../ActionDescription";
import { ErrorDescription } from "../../ErrorDescription";
import { ConvertWastrView } from "../context/ConvertWastrViewContext";

const selectorForERC20TokenConversionChangeValidation = ({
  accountBalance
}: AstridDaoStoreState) => ({
  accountBalance
});

type ERC20TokenConversionChangeValidationContext = ReturnType<
  typeof selectorForERC20TokenConversionChangeValidation
> & { wrappedTokenBalance: Decimal18 };

type ERC20TokenConversionChangeParams = {
  view: Omit<ConvertWastrView, "NO_APPROVAL">;
  currentAmount: Decimal18;
};

const ERC20TokenConversionChangeParamsDescription: React.FC<ERC20TokenConversionChangeParams> = ({
  currentAmount,
  view
}) => {
  const isViewDepositing = view === "DEPOSITING";
  const isViewWithdrawing = view === "WITHDRAWING";
  const isEmpty = currentAmount.isZero;

  return (
    <ActionDescription>
      {isViewDepositing && !isEmpty && (
        <>
          You will deposit <Amount>{currentAmount.prettify(TOKEN_PRECISION)} WASTR</Amount>
        </>
      )}
      {isViewWithdrawing && !isEmpty && (
        <>
          You will receive{" "}
          <Amount>
            {currentAmount.prettify(TOKEN_PRECISION)} {BASE_COLLAT}
          </Amount>
        </>
      )}
      .
    </ActionDescription>
  );
};

export const validateERC20TokenConversionChange = (
  view: string,
  originalAmount: ERC20TokenConversion,
  currentAmount: Decimal18,
  { accountBalance, wrappedTokenBalance }: ERC20TokenConversionChangeValidationContext
): [
  isValid: ERC20TokenConversionChange<Decimal18> | undefined,
  description: JSX.Element | undefined
] => {
  const change = originalAmount.whatChanged(currentAmount);

  if (!change) {
    return [undefined, undefined];
  }

  const isViewDepositing = view === "DEPOSITING";
  const isViewWithdrawing = view === "WITHDRAWING";

  if (isViewDepositing && currentAmount.gt(accountBalance)) {
    return [
      undefined,
      <ErrorDescription>
        The amount you're trying to deposit exceeds your balance by{" "}
        <Amount>
          {currentAmount.sub(accountBalance).prettify(TOKEN_PRECISION)} {BASE_COLLAT}
        </Amount>
        .
      </ErrorDescription>
    ];
  }

  if (isViewWithdrawing && currentAmount.gt(wrappedTokenBalance)) {
    return [
      undefined,
      <ErrorDescription>
        The amount you're trying to withdraw exceeds your balance by{" "}
        <Amount>{currentAmount.sub(wrappedTokenBalance).prettify(TOKEN_PRECISION)} WASTR</Amount>.
      </ErrorDescription>
    ];
  }

  return [
    change,
    <ERC20TokenConversionChangeParamsDescription view={view} currentAmount={currentAmount} />
  ];
};
